import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Download } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { generateSessionPDF } from '@/services/pdfGenerator';
import { trackEvent } from '@/services/eventTracker';

const getLevelInfo = (level) => {
  const levels = {
    1: { name: 'Rastgele Sinyal Baskınlığı', color: '#94a3b8' },
    2: { name: 'Gelişmekte Olan Sezgisel Sinyal', color: '#64748b' },
    3: { name: 'Gelişen Psişik Duyarlılık', color: '#06b6d4' },
    4: { name: 'Aktif Sezgisel Algı', color: '#8b5cf6' },
    5: { name: 'Yüksek Algısal Tutarlılık', color: '#a78bfa' }
  };
  return levels[level] || levels[1];
};

export const SessionDetail = ({ session, onBack }) => {
  const [isPremium] = React.useState(() => {
    return localStorage.getItem('zenermind_is_premium') === 'true';
  });
  
  const levelInfo = getLevelInfo(session.level);
  
  const handleDownloadPDF = () => {
    if (!isPremium) {
      alert('PDF dışa aktarma premium bir özelliktir. Bu işlevselliğe erişmek için yükseltin.');
      trackEvent.upgradeClicked('pdf_export');
      return;
    }
    
    generateSessionPDF(session);
    trackEvent.pdfRequested(session.sessionId);
  };
  
  // Prepare timeline data
  const timelineData = session.guessHistory.map((guess, index) => ({
    round: index + 1,
    result: guess.isHit ? 1 : 0,
    label: guess.isHit ? 'İsabet' : 'Kaçan'
  }));
  
  // Prepare symbol performance data
  const symbolData = Object.entries(session.symbolStats).map(([symbol, stats]) => ({
    symbol: symbol.charAt(0).toUpperCase() + symbol.slice(1),
    Tahmin: stats.guessed,
    Gerçek: stats.actual,
    İsabet: stats.hits
  }));
  
  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="session-detail-screen">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-slate-400 hover:text-slate-200 transition-colors"
              data-testid="detail-back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-heading text-slate-100">
                {session.sessionLabel}
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                {new Date(session.dateTime).toLocaleString('tr-TR')}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 rounded-full bg-white/5 px-6 py-3 text-sm font-medium text-white transition-all hover:bg-white/10 border border-white/10"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            {isPremium ? 'PDF İndir' : 'PDF Al (Premium)'}
          </button>
        </div>
        
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-panel p-6">
            <p className="text-slate-500 text-xs mb-2">Doğruluk</p>
            <p className="text-3xl font-heading text-violet-400">{session.accuracy}%</p>
          </div>
          <div className="glass-panel p-6">
            <p className="text-slate-500 text-xs mb-2">İsabet</p>
            <p className="text-3xl font-heading text-green-400">{session.hits}/{session.total}</p>
          </div>
          <div className="glass-panel p-6">
            <p className="text-slate-500 text-xs mb-2">En İyi Seri</p>
            <p className="text-3xl font-heading text-cyan-400">{session.bestStreak}</p>
          </div>
          <div className="glass-panel p-6">
            <p className="text-slate-500 text-xs mb-2">Seviye</p>
            <p className="text-3xl font-heading" style={{ color: levelInfo.color }}>
              {session.level}
            </p>
          </div>
        </div>
        
        {/* Hit/Miss Timeline */}
        <div className="glass-panel p-6 space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-violet-400 font-medium">
            İsabet/Kaçan Zaman Çizelgesi
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="round"
                stroke="#64748b"
                style={{ fontSize: '12px' }}
                label={{ value: 'Tur', position: 'insideBottom', offset: -5, fill: '#64748b' }}
              />
              <YAxis
                stroke="#64748b"
                style={{ fontSize: '12px' }}
                ticks={[0, 1]}
                tickFormatter={(value) => value === 1 ? 'İsabet' : 'Kaçan'}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                labelStyle={{ color: '#94a3b8' }}
                itemStyle={{ color: '#8b5cf6' }}
              />
              <Line type="stepAfter" dataKey="result" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Symbol Performance */}
        <div className="glass-panel p-6 space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-violet-400 font-medium">
            Sembol Performansı
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={symbolData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="symbol" stroke="#64748b" style={{ fontSize: '12px' }} />
              <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
              <Bar dataKey="Tahmin" fill="#06b6d4" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Gerçek" fill="#64748b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="İsabet" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Session Info */}
        <div className="glass-panel p-6 space-y-3">
          <h2 className="text-xs tracking-widest uppercase text-violet-400 font-medium">
            Oturum Bilgileri
          </h2>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-slate-500">Oturum ID: </span>
              <span className="text-slate-400 font-mono text-xs">{session.sessionId}</span>
            </div>
            {session.playerName && (
              <div>
                <span className="text-slate-500">Oyuncu: </span>
                <span className="text-slate-400">{session.playerName}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
